import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import { useOrder } from "../context/OrderContext";
import { useConfig } from "../context/ConfigContext";
import {
  API_URL,
  API_BASE_URL,
  SINGLE_ORDER,
  VENUE_ID,
  RAZORPAY_KEY_ID,
  getOrCreateCustomerToken,
} from "../config/constants";
import { PENDING_ORDER_KEY } from "../context/OrderContext";
import { formatPrice } from "../utils/currency";
import "./Checkout.css";

const PAYMOB_PENDING_KEY = "paymob_pending";
// Fallback for Egypt Paymob; KSA/OMN/UAE use different domains - backend returns correct iframeBase
const PAYMOB_IFRAME_BASE_FALLBACK =
  "https://accept.paymob.com/api/acceptance/iframes";

const Checkout = () => {
  const {
    paymentGateway,
    currency,
    razorpayKeyId,
    paymobUseFlash,
    paymobPublicKey,
    paymobUnifiedCheckoutBase,
  } = useConfig();
  const {
    cart,
    getCartTotal,
    clearCart,
    removeFromCart,
    setActiveOrder,
    setIsProcessing,
    socket,
    activeOrder,
    isProcessing,
    checkActiveOrder,
  } = useOrder();
  const navigate = useNavigate();
  const [processingPayment, setProcessingPayment] = useState(false);
  const [queueStatus, setQueueStatus] = useState(null);

  useEffect(() => {
    if (cart.length === 0) {
      navigate("/");
    }
  }, [cart.length, navigate]);

  // Refresh latest order status on mount - don't use stale state for payment button
  useEffect(() => {
    if (sessionStorage.getItem(PENDING_ORDER_KEY)) {
      checkActiveOrder();
    }
  }, [checkActiveOrder]);

  // Refresh latest order status before deciding - don't use stale state
  useEffect(() => {
    let mounted = true;
    // No longer redirect - allow checkout/payment even when previous order in progress
    return () => {
      mounted = false;
    };
  }, []);

  // Fetch queue status from backend (busy + ETA). Block new orders when kitchen busy.
  useEffect(() => {
    let mounted = true;
    const fetchQueueStatus = async () => {
      try {
        const resp = await axios.get(
          `${API_BASE_URL}/api/queue-status?venue_id=${VENUE_ID}`,
          { timeout: 8000 },
        );
        if (mounted) setQueueStatus(resp.data);
      } catch (e) {
        if (mounted) setQueueStatus(null);
        // Non-fatal: queue status unavailable, user can still browse/order
      }
    };

    fetchQueueStatus();
    const interval = setInterval(fetchQueueStatus, 10000);
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, []);

  // Real-time queue status updates via socket (filter by venue for multi-venue)
  useEffect(() => {
    if (!socket) return;
    const handleQueueStatusUpdate = (data) => {
      if (data?.venue_id != null && data.venue_id !== VENUE_ID) return;
      if (data) setQueueStatus(data);
    };
    socket.on("queueStatusUpdate", handleQueueStatusUpdate);
    return () => socket.off("queueStatusUpdate", handleQueueStatusUpdate);
  }, [socket]);

  const formatEta = (seconds) => {
    const s = Number(seconds || 0);
    if (!s || s <= 0) return null;
    const mins = Math.ceil(s / 60);
    if (mins < 60) return `${mins} min`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h}h ${m}m`;
  };

  // When kitchen is busy, block new orders (disable Pay). Exception: payment-in-progress completes.
  const kitchenBusy = queueStatus?.busy === true;

  const handlePayment = async () => {
    if (cart.length === 0) {
      toast.error("Cart is empty");
      return;
    }

    // Allow payment even when previous order in progress - new order will be queued

    // Queue policy: we no longer block payment when kitchen is busy. Order will be placed
    // and queued; it will start as soon as the current order completes.

    setProcessingPayment(true);

    try {
      const totalAmount = getCartTotal();
      const gateway = paymentGateway || "razorpay";

      if (gateway === "paymob") {
        const returnUrl = `${window.location.origin}/payment/return`;
        sessionStorage.setItem(
          PAYMOB_PENDING_KEY,
          JSON.stringify({
            items: cart.map((item) => ({
              recipe_id: item.recipe_id,
              quantity: item.quantity,
              recipe_price: item.recipe_price,
            })),
            customer_name: "Customer",
            customer_mobile: "+911234567890",
            customer_email: null,
          }),
        );

        if (paymobUseFlash && paymobPublicKey && paymobUnifiedCheckoutBase) {
          const intentionResp = await axios.post(
            `${API_BASE_URL}/api/paymob/create-intention`,
            {
              amount: totalAmount,
              currency: currency || "SAR",
              redirect_url: returnUrl,
            },
          );
          const { client_secret } = intentionResp.data;
          const unifiedUrl = `${paymobUnifiedCheckoutBase}/unifiedcheckout/?publicKey=${encodeURIComponent(paymobPublicKey)}&clientSecret=${encodeURIComponent(client_secret)}`;
          window.location.href = unifiedUrl;
          return;
        }

        const paymobResp = await axios.post(
          `${API_BASE_URL}/api/paymob/create-order`,
          {
            amount: totalAmount,
            currency: currency || "SAR",
            redirect_url: returnUrl,
          },
        );
        const { paymentToken, iframeId, iframeBase } = paymobResp.data;
        const redirectUrl = `${iframeBase || PAYMOB_IFRAME_BASE_FALLBACK}/${iframeId}?payment_token=${paymentToken}`;
        window.location.href = redirectUrl;
        return;
      }

      const razorpayResponse = await axios.post(
        `${API_BASE_URL}/api/razorpay/create-order`,
        { amount: totalAmount, currency: currency || "INR" },
      );

      const razorpayOrder = razorpayResponse.data;

      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => {
        const options = {
          key: razorpayKeyId || RAZORPAY_KEY_ID,
          amount: razorpayOrder.amount,
          currency: razorpayOrder.currency,
          name: "Barista AutoChef",
          description: "Order Payment",
          order_id: razorpayOrder.id,
          handler: async function (response) {
            try {
              const verifyResponse = await axios.post(
                `${API_BASE_URL}/api/razorpay/verify-payment`,
                {
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                },
              );

              if (verifyResponse.data.success) {
                await createOrder(verifyResponse.data.payment_id);
              } else {
                toast.error("Payment verification failed");
                setProcessingPayment(false);
              }
            } catch (error) {
              toast.error("Payment verification failed");
              setProcessingPayment(false);
            }
          },
          theme: { color: "#b87333" },
          modal: {
            ondismiss: function () {
              setProcessingPayment(false);
            },
          },
        };

        const razorpay = new window.Razorpay(options);
        razorpay.open();

        // Inject custom CSS to make modal fit content
        setTimeout(() => {
          const style = document.createElement("style");
          style.id = "razorpay-modal-custom";
          style.textContent = `
            .razorpay-container,
            .razorpay-checkout-frame,
            .razorpay-checkout-frame iframe {
              max-width: 90vw !important;
              max-height: 85vh !important;
              width: auto !important;
              height: auto !important;
            }
            .razorpay-checkout-frame {
              position: fixed !important;
              top: 50% !important;
              left: 50% !important;
              transform: translate(-50%, -50%) !important;
            }
            .razorpay-checkout-frame iframe {
              border-radius: 12px !important;
              box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3) !important;
            }
            @media (max-width: 768px) {
              .razorpay-container,
              .razorpay-checkout-frame,
              .razorpay-checkout-frame iframe {
                max-width: 95vw !important;
                max-height: 90vh !important;
              }
            }
          `;
          if (!document.getElementById("razorpay-modal-custom")) {
            document.head.appendChild(style);
          }
        }, 100);
      };
      document.body.appendChild(script);
    } catch (error) {
      toast.error("Failed to initiate payment");
      setProcessingPayment(false);
    }
  };

  const createOrder = async (paymentId) => {
    try {
      const orderItems = cart.map((item) => ({
        recipe_id: item.recipe_id,
        quantity: item.quantity,
        unit_price: item.recipe_price,
        toppings_json: null,
      }));

      const response = await axios.post(`${API_URL}/graphql`, {
        query: `
          mutation CreateOrder($input: CreateOrderInput!) {
            createOrder(input: $input) {
              order_id
              order_number
              order_status
              total_amount
              items {
                item_id
                recipe_id
                quantity
                recipe {
                  recipe_name
                }
              }
            }
          }
        `,
        variables: {
          input: {
            customer_name: "Customer",
            customer_mobile: "+911234567890",
            customer_email: null,
            customer_token: getOrCreateCustomerToken(),
            order_type: "customer_kiosk",
            payment_method: paymentGateway || "razorpay",
            payment_id: paymentId,
            kiosk_type: "customer",
            venue_id: VENUE_ID,
            items: orderItems,
          },
        },
      });

      if (response.data.data?.createOrder) {
        const order = response.data.data.createOrder;
        setActiveOrder(order);
        setIsProcessing(true);
        clearCart();
        sessionStorage.setItem(PENDING_ORDER_KEY, String(order.order_id));

        toast.success("Order placed successfully!");

        if (socket) {
          socket.emit("subscribeToOrder", { orderId: order.order_id });
        }

        setTimeout(() => {
          navigate(`/order-status/${order.order_id}`);
        }, 500);
      } else {
        throw new Error(
          response.data.errors?.[0]?.message || "Failed to create order",
        );
      }
    } catch (error) {
      const err = error.response?.data?.errors?.[0]?.message || error.message;
      const isNetwork = /timeout|network|ECONNREFUSED|ERR_NETWORK/i.test(
        String(err),
      );
      toast.error(
        isNetwork
          ? "Connection issue. Please check your network and try again."
          : err || "Failed to create order",
      );
      setProcessingPayment(false);
    }
  };

  // Always show payment when cart has items - new order will queue if kitchen busy
  const showPaymentButton = true;

  return (
    <div className="checkout-page">
      <div className="checkout-card">
        {/* Header */}
        <div className="checkout-header">
          <button className="back-btn" onClick={() => navigate("/")}>
            ← Menu
          </button>
          <h1>Checkout</h1>
          <button
            className="clear-cart-btn"
            onClick={() => {
              clearCart();
              navigate("/");
            }}
          >
            Clear
          </button>
        </div>

        {/* Order Items */}
        <div className="order-items">
          {cart.map((item) => (
            <div key={item.recipe_id} className="order-item">
              <div className="item-info">
                <span className="item-name">{item.recipe_name}</span>
                <span className="item-qty">×{item.quantity}</span>
              </div>
              <div className="item-actions">
                <span className="item-price">
                  {formatPrice(item.recipe_price * item.quantity, currency)}
                </span>
                <button
                  className="remove-item-btn"
                  onClick={() => removeFromCart(item.recipe_id)}
                  aria-label="Remove item"
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Total */}
        <div className="checkout-totals">
          <div className="checkout-total">
            <span>Total</span>
            <span className="total-amount">
              {formatPrice(getCartTotal(), currency)}
            </span>
          </div>
        </div>

        {/* Payment Button */}
        <div className="checkout-action">
          {queueStatus?.error && (
            <div className="kitchen-alert error">
              <strong>Kitchen needs attention</strong>
              <div className="kitchen-alert-msg">{queueStatus.error}</div>
            </div>
          )}

          {!SINGLE_ORDER && queueStatus?.etaSeconds ? (
            <div className="kitchen-alert info">
              <strong>Estimated wait</strong>
              <div className="kitchen-alert-msg">
                {formatEta(queueStatus.etaSeconds)} (based on current queue)
              </div>
            </div>
          ) : null}

          {kitchenBusy && (
            <div className="kitchen-alert info">
              <strong>Kitchen is busy</strong>
              <div className="kitchen-alert-msg">
                Your order will be queued. Estimated wait shown above.
              </div>
            </div>
          )}

          <button
            className="pay-btn"
            onClick={handlePayment}
            disabled={processingPayment || isProcessing}
          >
            {processingPayment ? (
              <>
                <span className="spinner">⏳</span> Processing...
              </>
            ) : (
              <>{`Pay ${formatPrice(getCartTotal(), currency)}`}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
